import { enqueueSnackbar } from "@powerdiary/components-library/lib/components/Snackbar";
import { AxiosError } from "axios";
import { Typography } from "@powerdiary/components-library/lib";
import { Box } from "@mui/material";
import { is400Error } from "./useClientPortalQuery";
import { ApiErrorResponse, UnhandledErrorDetailsApiDetailsErrorResponse } from "../types/generated/models";
export { showNetworkErrorSnackbar, resetNetworkErrorDebounce } from "../utils/networkErrorSnackbar";

interface AxiosErrorBase {
    error: AxiosError;
}

export interface ErrorPropsBase {
    logoutUser: () => Promise<void>;
    navigateTo: (path: string) => void;
    clientPortalGuid: string;
    userLoggedIn: boolean;
}

export type ErrorProps = ErrorPropsBase & AxiosErrorBase;

const ErrorMessageWrapper = ({ errorMessage }: { errorMessage: string }) => {
    return (
        <Box maxHeight="300px" sx={{ overflowY: "auto" }} data-test-id="alert_error_message">
            <Typography variant="paragraphLargeSemiBold">
                Please contact support with the steps to replicate.
            </Typography>
            <br />
            <br />
            <Typography variant="paragraphMediumRegular">{errorMessage}</Typography>
        </Box>
    );
};

const handle401 = ({ userLoggedIn, logoutUser }: ErrorProps) => {
    if (userLoggedIn) {
        void logoutUser();
        enqueueSnackbar("Your login has expired. Please login again.", {
            alertTitle: "Session expired",
            variant: "error",
        });
    }
};

const handle500 = ({ error }: ErrorProps) => {
    const contentType = (error.response.headers?.["content-type"] as string) ?? "";
    const isHtmlResponse = error.response.status === 502 || contentType.includes("text/html");

    if (isHtmlResponse) {
        enqueueSnackbar(<ErrorMessageWrapper errorMessage="Something went wrong, please try again." />, {
            variant: "error",
            alertTitle: "There has been an issue with the server",
        });
        return;
    }

    let errorMessage: string;
    const data = error.response.data as { message?: string; error?: string } | string;
    if (typeof data === "string") {
        errorMessage = data;
    } else if (typeof data === "object" && data !== null) {
        // Try to extract a message property, fallback to JSON string
        errorMessage = data.message || data.error || JSON.stringify(data);
    } else {
        errorMessage = "An unknown error occurred.";
    }
    enqueueSnackbar(<ErrorMessageWrapper errorMessage={errorMessage} />, {
        variant: "error",
        alertTitle: "There has been an issue with the server",
    });
};

const handle404 = ({ navigateTo, clientPortalGuid }: ErrorProps) => {
    void navigateTo(`/clientportal/${clientPortalGuid}/not-available`);
};

function handleOther400({ error }: ErrorProps) {
    if (typeof error.response.data === "string") {
        enqueueSnackbar(<Typography variant="paragraphMediumRegular">{error.response.data}</Typography>, {
            variant: "error",
            alertTitle: "Request error",
        });
    } else if (typeof error.response.data === "object" && error.response.data !== null) {
        const errorData = error.response.data as ApiErrorResponse;
        enqueueSnackbar(<Typography variant="paragraphMediumRegular">{errorData.message}</Typography>, {
            variant: "error",
            alertTitle: "Request error",
        });
    } else {
        enqueueSnackbar(
            <Typography variant="paragraphMediumRegular">There has been an issue with request. </Typography>,
            {
                variant: "error",
                alertTitle: "Request error",
            }
        );
    }
}

function getServerRedirectUrl(error: ErrorProps): string | undefined {
    const data = error.error.response?.data as UnhandledErrorDetailsApiDetailsErrorResponse | string | undefined;
    if (typeof data !== "object" || data === null) {
        return undefined;
    }
    return data.details?.redirectUrl ?? undefined;
}

function handleResponseError(error: ErrorProps) {
    const redirectUrl = getServerRedirectUrl(error);
    if (redirectUrl) {
        window.location.href = redirectUrl;
        return;
    }

    if (error.error.response.status === 401) {
        handle401(error);
    } else if (error.error.response.status === 404) {
        handle404(error);
    } else if (is400Error(error.error)) {
        handleOther400(error);
    } else if (error.error.response.status >= 500) {
        handle500(error);
    }
}

import { showNetworkErrorSnackbar } from "../utils/networkErrorSnackbar";

function handleRequestError({ error }: ErrorProps) {
    enqueueSnackbar(
        <>
            <Typography variant="paragraphMediumRegular">{error.message}</Typography>
            <br />
            <Typography variant="paragraphLargeSemiBold">
                Please contact support with the steps to replicate.
            </Typography>
        </>,
        {
            variant: "error",
            alertTitle: "Error",
        }
    );
}

export function handleAxiosError(errorProps: ErrorProps) {
    const axiosError = errorProps.error;

    if (axiosError.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        handleResponseError(errorProps);
    } else if (axiosError.request) {
        // The request was made but no response was received
        showNetworkErrorSnackbar();
    } else {
        // Something happened in setting up the request before it was sent
        handleRequestError(errorProps);
    }

    throw axiosError;
}
